import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { MockFooterComponent } from '@core/testing/footer.mock';
import { NgbDateAdapter, NgbDateParserFormatter, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import {
  BrandedSetSchoolIdsFormComponent,
} from '@shared/branded-set-school-ids-form/branded-set-school-ids-form.component';
import { ButtonComponent } from '@shared/button/button.component';
import { ModalTemplateComponent } from '@shared/modals/modal-template/modal-template.component';
import {
  UserAnnouncementModalComponent
} from '@shared/modals/user-announcement-modal/user-announcement-modal.component';
import {
  OverviewAdminActionsComponent
} from '@shared/overview/overview-admin-actions/overview-admin-actions.component';
import {
  OverviewAdminResourcesComponent
} from '@shared/overview/overview-admin-resources/overview-admin-resources.component';
import {
  OverviewGettingStartedChecklistComponent
} from '@shared/overview/overview-getting-started-checklist/overview-getting-started-checklist.component';
import { OverviewMyProductsComponent } from '@shared/overview/overview-my-products/overview-my-products.component';
import { OverviewWelcomeComponent } from '@shared/overview/overview-welcome/overview-welcome.component';
import { PreloaderModule } from '@shared/preloader/preloader.module';
import { SelectComponent } from '@shared/select/select.component';
import { UsersTableComponent } from '@shared/users-table/users-table.component';
import { ZbpIconComponent } from '@shared/zbp-icon/zbp-icon';
import { EditorModule } from '@tinymce/tinymce-angular';
import { LoginInformationCtaComponent } from '../login/login-information-cta/login-information-cta.component';
import { AddLicensesFormComponent } from './add-licenses-form/add-licenses-form';
import {
  AdminLicenseStartEndDateTableComponent
} from './admin-license-start-end-date-table/admin-license-start-end-date-table.component';
import { AlertBarContainerComponent } from './alert-bar-container/alert-bar-container.component';
import { AriaHiddenTooltipDirective } from './aria-hidden-tooltip.directive';
import { BackToWebsiteLinkComponent } from './back-to-website-link/back-to-website-link.component';
import { BlockedDirective } from './blocked.directive';
import { BrandedFallbackDownloadComponent } from './branded-fallback-download/branded-fallback-download.component';
import {
  BrandedPasswordRequirementsComponent
} from './branded-password-requirements/branded-password-requirements.component';
import {
  BenchmarkReportsStateService
} from './branded-reports/branded-benchmark-reports/benchmark-reports-state-service';
import {
  BrandedProgressReportsComponent
} from './branded-reports/branded-progress-reports/branded-progress-reports.component';
import {
  BrandedReportsHelpModalComponent
} from './branded-reports/branded-reports-help-modal/branded-reports-help-modal.component';
import { BrandedStandardsComponent } from './branded-standards/branded-standards.component';
import { ColorPickerComponent } from './color-picker/color-picker.component';
import { IsoDatePickerAdapter } from './date-picker/iso-date-picker-adapter';
import { NonIsoDateFormatter } from './date-picker/non-iso-date-formatter';
import { DroppableDirective } from './droppable.directive';
import { EducationalUnitService } from './educational-unit/educational-unit.service';
import { FallbackDownloadComponent } from './fallback-download/fallback-download.component';
import { FileInputComponent } from './file-input/file-input.component';
import { FooterComponent } from './footer/footer.component';
import { BrowserGlobalRef, GlobalRef } from './global-ref';
import { SubnavigationComponent } from './header/branded-header/subnavigation/subnavigation.component';
import { IsoDatePipe } from './iso-date/iso-date.pipe';
import { LessonPlannerComponent } from './lesson-planner/lesson-planner.component';
import { LicenseExpiredPopOverComponent } from './license-expired-pop-over/license-expired-pop-over.component';
import { LoadingPlaceholderComponent } from './loading-placeholder/loading-placeholder.component';
import { MakeTextFitDirective } from './make-text-fit.directive';
import { BrandedAdminsTableComponent } from './manage-users/admins/branded-admins-table/branded-admins-table.component';
import {
  BrandedCreateSchoolUsersComponent
} from './manage-users/branded-create-school-users/branded-create-school-users.component';
import { BrandedManageUsersComponent } from './manage-users/branded-manage-users/branded-manage-users.component';
import {
  BrandedUserManagementTableComponent
} from './manage-users/branded-user-management-table/branded-user-management-table.component';
import {
  ManageUsersFormComponent
} from './manage-users/manage-users-form/manage-users-form.component';
import {
  BrandedParentsTableComponent
} from './manage-users/parents/branded-parents-table/branded-parents-table.component';
import {
  BrandedStudentsTableComponent
} from './manage-users/students/branded-students-table/branded-students-table.component';
import {
  CreateStudentPasswordsModalComponent
} from './manage-users/students/create-student-passwords-modal/create-student-passwords-modal.component';
import {
  BrandedTeachersTableComponent
} from './manage-users/teachers/branded-teachers-table/branded-teachers-table.component';
import { MarkdownService } from './markdown.service';
import { GenericConfirmModalComponent } from './modals/generic-confirm-modal/generic-confirm-modal.component';
import { QrCodeHelpModalComponent } from './modals/qr-code-help-modal/qr-code-help-modal.component';
import { UnsavedChangesModalComponent } from './modals/unsaved-changes/unsaved-changes-modal.component';
import { NoLicenseComponent } from './no-license/no-license.component';
import { NumberInputComponent } from './number-input/number-input.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { PdfAutoPrintComponent } from './pdf-auto-print/pdf-auto-print.component';
import { PinProgressBarComponent } from './pin-progress-bar/pin-progress-bar.component';
import { ObjToKeyValPairsPipe } from './pipes/obj-to-key-val-pairs.pipe';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { StripHtmlPipe } from './pipes/strip-html.pipe';
import {
  ProductEventNotificationModalComponent
} from './product-event-notification-modal/product-event-notification-modal.component';
import { QuestLaunchDirective } from './quest-launch.directive';
import { SortColumnsService } from './sort-columns.service';
import { StarProgressBarComponent } from './star-progress-bar/star-progress-bar.component';
import { StudentAudioButtonComponent } from './student-audio-button/student-audio-button.component';
import { StudentAvatarComponent } from './student-avatar/student-avatar.component';
import { StudentAvatarModalComponent } from './student-avatar-modal/student-avatar-modal.component';
import { StudentAvatarStoryModalComponent } from './student-avatar-story-modal/student-avatar-story-modal.component';
import {
  SubscriptionInformationComponent
} from './subscription-information-component/subscription-information.component';
import { TextSearchComponent } from './text-search/text-search.component';
import { UserAnnouncementsComponent } from './user-announcements/user-announcements.component';
import { ViewerComponent } from './viewer/viewer.component';
import { ViewerService } from './viewer/viewer.service';
import { BrandedWistiaComponent } from './wistia/branded-wistia/branded-wistia.component';
import { WistiaComponent } from './wistia/wistia.component';
import { WistiaService } from './wistia/wistia.service';
import { ZbSwirlDirective } from './zb-swirl.directive';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    NgbModule,
    NgSelectModule,
    RouterModule,
    ReactiveFormsModule,
    TranslateModule,
    EditorModule,
    ZbpIconComponent,
    NgOptimizedImage,
    ButtonComponent,
    AdminLicenseStartEndDateTableComponent,
    PreloaderModule,
    TextSearchComponent,
    SubscriptionInformationComponent,
    AlertBarContainerComponent,
    ManageUsersFormComponent,
    AddLicensesFormComponent,
    UserAnnouncementsComponent,
    ModalTemplateComponent,
    UsersTableComponent,
    SubnavigationComponent,
    BrandedProgressReportsComponent,
    BrandedReportsHelpModalComponent,
    UnsavedChangesModalComponent,
    NumberInputComponent,
    BrandedFallbackDownloadComponent,
    ViewerComponent,
    BrandedWistiaComponent,
    LoadingPlaceholderComponent,
    BrandedStandardsComponent,
    FooterComponent,
    PdfAutoPrintComponent,
    SelectComponent,
  ],
  declarations: [
    BlockedDirective,
    FallbackDownloadComponent,
    LicenseExpiredPopOverComponent,
    MakeTextFitDirective,
    ObjToKeyValPairsPipe,
    PageNotFoundComponent,
    QuestLaunchDirective,
    SafeHtmlPipe,
    StarProgressBarComponent,
    StudentAvatarComponent,
    StudentAvatarModalComponent,
    StudentAvatarStoryModalComponent,
    WistiaComponent,
    ZbSwirlDirective,
    ColorPickerComponent,
    BrandedCreateSchoolUsersComponent,
    NoLicenseComponent,
    PinProgressBarComponent,
    StripHtmlPipe,
    DroppableDirective,
    LessonPlannerComponent,
    StudentAudioButtonComponent,
    QrCodeHelpModalComponent,
    IsoDatePipe,
    OverviewWelcomeComponent,
    OverviewGettingStartedChecklistComponent,
    OverviewAdminResourcesComponent,
    OverviewMyProductsComponent,
    OverviewAdminActionsComponent,
    GenericConfirmModalComponent,
    BrandedSetSchoolIdsFormComponent,
    BrandedManageUsersComponent,
    BrandedTeachersTableComponent,
    BrandedUserManagementTableComponent,
    BrandedAdminsTableComponent,
    BrandedStudentsTableComponent,
    BrandedParentsTableComponent,
    FileInputComponent,
    UserAnnouncementModalComponent,
    BackToWebsiteLinkComponent,
    ProductEventNotificationModalComponent,
    BrandedPasswordRequirementsComponent,
    LoginInformationCtaComponent,
    CreateStudentPasswordsModalComponent,
    MockFooterComponent,
    AriaHiddenTooltipDirective,
  ],
  exports: [
    BlockedDirective,
    ColorPickerComponent,
    CommonModule,
    FallbackDownloadComponent,
    FooterComponent,
    FormsModule,
    LicenseExpiredPopOverComponent,
    MakeTextFitDirective,
    NgbModule,
    NgSelectModule,
    ObjToKeyValPairsPipe,
    StripHtmlPipe,
    QuestLaunchDirective,
    ReactiveFormsModule,
    StarProgressBarComponent,
    StudentAvatarComponent,
    ZbSwirlDirective,
    PinProgressBarComponent,
    LessonPlannerComponent,
    DroppableDirective,
    TranslateModule,
    StudentAudioButtonComponent,
    IsoDatePipe,
    SafeHtmlPipe,
    OverviewWelcomeComponent,
    OverviewGettingStartedChecklistComponent,
    OverviewAdminResourcesComponent,
    OverviewMyProductsComponent,
    OverviewAdminActionsComponent,
    BrandedSetSchoolIdsFormComponent,
    BrandedCreateSchoolUsersComponent,
    BrandedManageUsersComponent,
    BrandedTeachersTableComponent,
    BrandedUserManagementTableComponent,
    BrandedAdminsTableComponent,
    BrandedStudentsTableComponent,
    BrandedParentsTableComponent,
    TextSearchComponent,
    FileInputComponent,
    BrandedFallbackDownloadComponent,
    NumberInputComponent,
    BackToWebsiteLinkComponent,
    ProductEventNotificationModalComponent,
    BrandedPasswordRequirementsComponent,
    AlertBarContainerComponent,
    LoginInformationCtaComponent,
    CreateStudentPasswordsModalComponent,
    SubnavigationComponent,
    UnsavedChangesModalComponent,
    AriaHiddenTooltipDirective,
    SelectComponent,
    PageNotFoundComponent,
  ],
  providers: [
    EducationalUnitService,
    MarkdownService,
    SortColumnsService,
    Title,
    WistiaService,
    BenchmarkReportsStateService,
    ViewerService,
    { provide: NgbDateAdapter, useClass: IsoDatePickerAdapter },
    { provide: NgbDateParserFormatter, useClass: NonIsoDateFormatter },
  ],
})
export class SharedModule {
  static forBrowser(): ModuleWithProviders<any> {
    return {
      ngModule: SharedModule,
      providers: [
        { provide: GlobalRef, useClass: BrowserGlobalRef }
      ]
    };
  }
}
